'use client';

import { useMemo, useRef, useState } from 'react';
import {
  Menu,
  BodyDefault,
  IconButton,
  LabelDefault,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack } from '@terra/ui-styled-system';
import { ArrowRight01Icon, LinkSquare02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { Application } from '../../../store/projectStore';
import { MenuDivider } from '../MenuDivider';
import { SidebarSubmenu } from '../SidebarSubmenu';
import type { AttackSurfaceConfig } from './attackSurfaces';
import { compareByName } from './compareByName';
import { SEARCH_VISIBILITY_THRESHOLD } from './constants';
import { TargetSelectorMenuItemRow } from './TargetSelectorMenuItemRow';
import { TargetSelectorSearchInput } from './TargetSelectorSearchInput';

type AttackSurfaceSubmenuProps = {
  surface: AttackSurfaceConfig;
  label: string;
  targets: Application[];
  currentTargetId: string;
  currentTargetType: TargetTypeType;
  onSelectTarget: (target: Application) => void;
  onOpenInNewWindow?: (target: Application) => void;
};

export function AttackSurfaceSubmenu({
  surface,
  label,
  targets,
  currentTargetId,
  currentTargetType,
  onSelectTarget,
  onOpenInNewWindow,
}: AttackSurfaceSubmenuProps) {
  const tSelector = useTranslations('proxy.applicationSelector');
  const tWorkspaces = useTranslations('proxy.workspaces');
  const [searchQuery, setSearchQuery] = useState('');
  const triggerRef = useRef<HTMLDivElement>(null);
  const { icon: Icon } = surface;
  const isCurrentSurface = currentTargetType === surface.targetType;
  const currentTarget = useMemo(
    () =>
      isCurrentSurface
        ? targets.find((target) => target.id === currentTargetId)
        : undefined,
    [targets, currentTargetId, isCurrentSurface],
  );
  const sortedTargets = useMemo(
    () => [...targets].sort(compareByName),
    [targets],
  );
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredTargets = useMemo(() => {
    if (!normalizedQuery) {
      return sortedTargets;
    }

    return sortedTargets.filter((target) =>
      target.name.toLowerCase().includes(normalizedQuery),
    );
  }, [sortedTargets, normalizedQuery]);
  const isLongList = targets.length > SEARCH_VISIBILITY_THRESHOLD;
  const showPinnedCurrent = !normalizedQuery && Boolean(currentTarget);
  const listTargets =
    showPinnedCurrent && currentTarget
      ? filteredTargets.filter((target) => target.id !== currentTarget.id)
      : filteredTargets;
  const openInNewWindowLabel = tWorkspaces('openInNewWindow');
  const renderTargetItem = (target: Application, isCurrent: boolean) => (
    <Menu.Item
      key={target.id}
      value={target.id}
      onClick={() => onSelectTarget(target)}
      p="2"
    >
      <TargetSelectorMenuItemRow
        icon={<Icon size={14} />}
        label={target.name}
        showCheck={isCurrent}
        hoverAction={
          !isCurrent && onOpenInNewWindow ? (
            <Tooltip title={openInNewWindowLabel}>
              <IconButton
                variant="ghost"
                size="2xs"
                stopPropagation
                onPointerUp={(event) => event.stopPropagation()}
                aria-label={openInNewWindowLabel}
                onClick={() => onOpenInNewWindow(target)}
              >
                <LinkSquare02Icon size={12} />
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      />
    </Menu.Item>
  );

  return (
    <SidebarSubmenu
      triggerRef={triggerRef}
      trigger={
        <Menu.TriggerItem ref={triggerRef} p="2">
          <HStack gap="2" flexGrow={1} alignItems="center" minW="0">
            <Box
              w="14px"
              h="14px"
              flexShrink={0}
              display="flex"
              alignItems="center"
              color="fg.neutral.default"
            >
              <Icon size={14} />
            </Box>
            <BodyDefault truncate>{label}</BodyDefault>
          </HStack>
          <HStack gap="1" alignItems="center" flexShrink={0}>
            <Box
              minW="5"
              minH="5"
              px="1"
              py="0.5"
              borderRadius="full"
              bg="bg.neutral.muted"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <MetaDefault color="fg.neutral.default">
                {targets.length}
              </MetaDefault>
            </Box>
            <Menu.Indicator>
              <ArrowRight01Icon size={14} />
            </Menu.Indicator>
          </HStack>
        </Menu.TriggerItem>
      }
    >
      <Box px="2" py="1">
        <LabelDefault as="span" color="fg.neutral.muted" truncate>
          {`${label} (${targets.length})`}
        </LabelDefault>
      </Box>
      {isLongList && (
        <Box px="2" pb="1">
          <TargetSelectorSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={tSelector('searchApplications')}
          />
        </Box>
      )}
      <MenuDivider />
      {showPinnedCurrent && currentTarget && (
        <>
          <Menu.ItemGroup>
            {renderTargetItem(currentTarget, true)}
          </Menu.ItemGroup>
          <MenuDivider />
        </>
      )}
      {listTargets.length > 0 ? (
        <Menu.ItemGroup
          {...(isLongList ? { maxH: '280px', overflowY: 'auto' as const } : {})}
          gap="0"
        >
          {listTargets.map((target) =>
            renderTargetItem(
              target,
              isCurrentSurface && target.id === currentTargetId,
            ),
          )}
        </Menu.ItemGroup>
      ) : showPinnedCurrent ? null : (
        <Box px="3" py="3" textAlign="center">
          <BodyDefault color="fg.neutral.muted">
            {tSelector('noApplicationsFound')}
          </BodyDefault>
        </Box>
      )}
    </SidebarSubmenu>
  );
}
